import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { FiFile, FiEye, FiDownload, FiInbox, FiSearch, FiLogOut, FiInfo, FiAlertTriangle, FiTrendingUp, FiX, FiCheckCircle, FiFileText, FiXCircle, FiRefreshCw } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API_BASE_URL, API_DOMAIN } from "../utils/config";
import { toast } from "react-toastify";
import { jsPDF } from "jspdf";
import "./EmployeeDashboard.css";
import { FaFilePdf, FaFileContract } from "react-icons/fa";

const SECTION_CONFIG = {
  Offer: {
    label: "Offer Letters",
    icon: <FiCheckCircle size={15} />,
    gradientFrom: "from-emerald-500",
    gradientTo: "to-teal-600",
    badge: "bg-emerald-100 text-emerald-700",
    emptyMsg: "No offer letters have been issued yet.",
  },
  Appraisal: {
    label: "Appraisal Letters",
    icon: <FiTrendingUp size={15} />,
    gradientFrom: "from-blue-500",
    gradientTo: "to-indigo-600",
    badge: "bg-blue-100 text-blue-700",
    emptyMsg: "No appraisal letters have been issued yet.",
  },
  Warning: {
    label: "Warning Letters",
    icon: <FiAlertTriangle size={15} />,
    gradientFrom: "from-amber-500",
    gradientTo: "to-orange-600",
    badge: "bg-amber-100 text-amber-700",
    emptyMsg: "No warning letters have been issued yet.",
  },
  Termination: {
    label: "Termination Letters",
    icon: <FiXCircle size={15} />,
    gradientFrom: "from-red-500",
    gradientTo: "to-rose-600",
    badge: "bg-red-100 text-red-700",
    emptyMsg: "No termination letters have been issued yet.",
  },
  Resignation: {
    label: "Resignation Letters",
    icon: <FiLogOut size={15} />,
    gradientFrom: "from-purple-500",
    gradientTo: "to-fuchsia-600",
    badge: "bg-purple-100 text-purple-700",
    emptyMsg: "No approved resignations on record.",
  },
  Agreement: {
    label: "Agreements",
    icon: <FiFileText size={15} />,
    gradientFrom: "from-indigo-500",
    gradientTo: "to-purple-600",
    badge: "bg-indigo-100 text-indigo-700",
    emptyMsg: "No agreements have been issued yet.",
  },
};

const SECTION_ORDER = ["Offer", "Appraisal", "Warning", "Termination", "Agreement", "Resignation"];

const Letters = () => {
  const {
    appliedJobs,
    searchQuery: globalSearchQuery,
    handleDownloadOffer,
    setIsModalOpen,
    setSelectedOffer,
    setActiveDocTab,
  } = useOutletContext();

  const [localSearchQuery, setLocalSearchQuery] = useState("");
  const [isResignModalOpen, setIsResignModalOpen] = useState(false);
  const [resignationLetter, setResignationLetter] = useState("");
  const [isSubmittingResign, setIsSubmittingResign] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedAppForUpload, setSelectedAppForUpload] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [signMethod, setSignMethod] = useState("upload");
  const [signatureName, setSignatureName] = useState("");
  const [selectedFont, setSelectedFont] = useState("'Dancing Script', cursive");
  const [signaturePreview, setSignaturePreview] = useState(null);
  const { fetchDashboardData, profile, logoUrl } = useOutletContext();
  const [stampUrl, setStampUrl] = useState("");

  useEffect(() => {
    const fetchStamp = async () => {
      try {
        const token = localStorage.getItem("candidateToken");
        const res = await axios.get(`${API_BASE_URL}/admin/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.success && res.data.settings && res.data.settings.companyStamp) {
          const stamp = res.data.settings.companyStamp;
          const fullStampUrl = stamp.startsWith('http') ? stamp : `${API_DOMAIN}${stamp}`;
          setStampUrl(fullStampUrl);
        }
      } catch (err) {
        console.error("Failed to fetch stamp:", err);
      }
    };
    fetchStamp();
  }, []);

  const canResignApp = appliedJobs.find(
    (app) =>
      (app.status === "Selected" || app.status === "Hired") &&
      app.resignationStatus !== "Approved"
  );

  const allLetters = [];

  appliedJobs.forEach((app) => {
    if (app.documentHistory && app.documentHistory.length > 0) {
      app.documentHistory.forEach((hist, idx) => {
        const type = hist.documentType || "Offer";
        if (SECTION_CONFIG[type]) {
          allLetters.push({
            ...app,
            _uid: `${app._id}-hist-${idx}`,
            resolvedDocType: type,
            resolvedContent: hist.content,
            resolvedDate: hist.sentAt || app.offerSentAt || app.updatedAt,
            attachmentPath: hist.attachmentPath || (idx === (app.documentHistory.length - 1) ? app.adminAttachment : ""),
            signedPath: hist.signedPath,
            signedAt: hist.signedAt
          });
        }
      });
    } 
    
    const hasOffer = !!app.offerLetter;
    const hasAgreement = !!(app.adminAgreements || (app.adminAttachment && app.documentType === "Agreement"));
    const historyTypes = (app.documentHistory || []).map(h => h.documentType);
    
    if (hasOffer && !historyTypes.includes("Offer")) {
        allLetters.push({
            ...app,
            _uid: `${app._id}-single-offer`,
            resolvedDocType: "Offer",
            resolvedContent: app.offerLetter,
            resolvedDate: app.offerSentAt || app.updatedAt,
            attachmentPath: app.adminAttachment,
            signedPath: app.candidateAgreementsUpload && app.documentType === "Offer" ? app.candidateAgreementsUpload : "",
        });
    }
    
    if (hasAgreement && !historyTypes.includes("Agreement")) {
        allLetters.push({
            ...app,
            _uid: `${app._id}-single-agreement`,
            resolvedDocType: "Agreement",
            resolvedContent: app.adminAgreements || "Attachment Sent",
            resolvedDate: app.updatedAt,
            attachmentPath: app.adminAttachment,
            signedPath: app.candidateAgreementsUpload && app.documentType === "Agreement" ? app.candidateAgreementsUpload : "",
        });
    }

    if (app.status === "Resigned" && app.resignationStatus === "Approved") {
      allLetters.push({
        ...app,
        _uid: `${app._id}-resign`,
        resolvedDocType: "Resignation",
        resolvedContent: app.resignationLetter,
        resolvedDate: app.resignationSentAt || app.updatedAt,
      });
    }
  });

  const combinedQuery = (globalSearchQuery || localSearchQuery).toLowerCase();
  const filtered = allLetters.filter((entry) => {
    if (!combinedQuery) return true;
    return (
      (entry.jobId?.role || entry.role || "").toLowerCase().includes(combinedQuery) ||
      entry.resolvedDocType.toLowerCase().includes(combinedQuery)
    );
  });

  const grouped = {};
  SECTION_ORDER.forEach((t) => (grouped[t] = []));
  filtered.forEach((entry) => {
    if (grouped[entry.resolvedDocType]) grouped[entry.resolvedDocType].push(entry);
  });

  const handleDownloadRelieving = (app) => {
    try {
      const doc = new jsPDF();
      doc.setFontSize(20);
      doc.text("Relieving Letter", 105, 20, { align: "center" });
      doc.setFontSize(12);
      doc.text(`Date: ${new Date().toLocaleDateString()}`, 20, 40);
      doc.text(`To: ${app.firstName} ${app.lastName}`, 20, 50);
      doc.text("This is to certify that you have been relieved from your duties", 20, 70);
      doc.text(`as ${app.jobId?.role || "Employee"} at Timely Health effective ${new Date(app.updatedAt).toLocaleDateString()}.`, 20, 80);
      doc.text("We wish you the best in your future endeavors.", 20, 100);
      doc.text("Sincerely,", 20, 120);
      doc.text("HR Department", 20, 130);
      doc.text("Timely Health", 20, 140);
      doc.save(`Relieving_Letter_${app.firstName}_${app.lastName}.pdf`);
      toast.success("Relieving letter downloaded");
    } catch (err) {
      console.error("Error generating relieving letter:", err);
      toast.error("Failed to generate relieving letter");
    }
  };

  const handleResign = async (e) => {
    e.preventDefault();
    if (!resignationLetter.trim() || !canResignApp) return;
    setIsSubmittingResign(true);
    try {
      const token = localStorage.getItem("candidateToken");
      await axios.put(
        `${API_BASE_URL}/candidate/resign`,
        { applicationId: canResignApp._id, resignationLetter, resignationReason: "Personal" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Resignation submitted successfully.");
      setIsResignModalOpen(false);
      setResignationLetter("");
      window.location.reload();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to submit resignation.");
    } finally {
      setIsSubmittingResign(false);
    }
  };

  const getBase64ImageFromUrl = (imgUrl) => {
    return new Promise((resolve) => {
      if (!imgUrl) {
        resolve(null);
        return;
      }
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve({ dataUrl: canvas.toDataURL("image/png"), width: img.width, height: img.height });
      };
      img.onerror = () => resolve(null);
      img.src = imgUrl;
    });
  };

  const generateSignedPdfBlob = async (entry, signatureDataUrl) => {
    const content = entry.resolvedContent;
    const type = entry.resolvedDocType || "Official";
    const sentAt = entry.resolvedDate || new Date();

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - (2 * margin);

    doc.setFillColor(248, 250, 252);
    doc.rect(0, 0, pageWidth, 45, 'F');
    
    const logoBase64 = await getBase64ImageFromUrl(logoUrl);
    if (logoBase64 && logoBase64.dataUrl) {
      const ratio = logoBase64.width / logoBase64.height;
      const imgHeight = 12;
      const imgWidth = imgHeight * ratio;
      doc.addImage(logoBase64.dataUrl, "PNG", margin, 15, imgWidth, imgHeight);
    } else {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.setTextColor(30, 41, 59);
      doc.text("TIMELY HEALTH", margin, 25);
    }

    const headerTitle = type.toUpperCase();
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(63, 81, 181);
    doc.text(`${headerTitle} LETTER`, pageWidth - margin, 25, { align: "right" });

    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.5);
    doc.line(margin, 45, pageWidth - margin, 45);

    let currentY = 60;
    doc.setFontSize(10);
    doc.setTextColor(71, 85, 105);
    doc.text(`Issued On: ${new Date(sentAt).toLocaleDateString('en-GB')}`, pageWidth - margin, currentY, { align: "right" });

    doc.setFont("helvetica", "bold");
    doc.setTextColor(30, 41, 59);
    doc.text("TO:", margin, currentY);
    doc.setFont("helvetica", "normal");
    currentY += 6;
    const fullName = profile?.name || `${entry.firstName} ${entry.lastName}`;
    doc.text(fullName, margin, currentY);
    currentY += 15;

    const sanitizedContent = content
      .replace(/\*\*/g, "") 
      .replace(/\*/g, "")   
      .replace(/#{1,6}\s?/g, "")
      .replace(/~~/g, "");

    doc.setFontSize(11);
    doc.setTextColor(51, 65, 85);
    const lines = doc.splitTextToSize(sanitizedContent, contentWidth);
    
    lines.forEach(line => {
      if (currentY > pageHeight - 80) {
        doc.addPage();
        currentY = 30;
      }
      doc.text(line, margin, currentY);
      currentY += 6.5;
    });

    currentY += 20;
    if (currentY > pageHeight - 70) {
      doc.addPage();
      currentY = 40;
    }

    // Signatures - WITH STAMP ON LEFT SIDE
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    
    // ADD STAMP ABOVE AUTHORIZED SIGNATORY
    if (stampUrl) {
      const stampBase64 = await getBase64ImageFromUrl(stampUrl);
      if (stampBase64 && stampBase64.dataUrl) {
        const stampRatio = stampBase64.width / stampBase64.height;
        const stampHeight = 18;
        const stampWidth = stampHeight * stampRatio;
        doc.addImage(stampBase64.dataUrl, "PNG", margin, currentY - 15, stampWidth, stampHeight);
      }
    }
    
    doc.text("Authorized Signatory,", margin, currentY);
    currentY += 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("Recruitment Division | Timely Health", margin, currentY);
    
    const rightSide = pageWidth - margin - 60;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("Candidate Signature,", rightSide, currentY - 5);
    
    if (signatureDataUrl) {
      doc.addImage(signatureDataUrl, 'PNG', rightSide, currentY, 50, 20);
    }

    return doc.output('blob');
  };

  const handleFileUpload = async (blob) => {
    if (!selectedAppForUpload) return;
    if (signMethod === "upload" && !selectedFile) return;

    setIsUploading(true);
    const formData = new FormData();
    
    if (signMethod === "digital" && blob) {
      const fileName = `${selectedAppForUpload.resolvedDocType}_Signed_${Date.now()}.pdf`;
      formData.append("signedDoc", blob, fileName);
    } else {
      formData.append("signedDoc", selectedFile);
    }
    
    formData.append("applicationId", selectedAppForUpload._id);
    formData.append("documentType", selectedAppForUpload.resolvedDocType);

    try {
      const token = localStorage.getItem("candidateToken");
      const res = await axios.post(`${API_BASE_URL}/applications/upload-signed-docs`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`
        }
      });

      if (res.data.success) {
        toast.success(`${selectedAppForUpload.resolvedDocType} signed and uploaded successfully!`);
        setIsUploadModalOpen(false);
        setSelectedFile(null);
        setSignatureName("");
        setSignaturePreview(null);
        if (fetchDashboardData) fetchDashboardData(); 
        else window.location.reload();
      }
    } catch (err) {
      console.error("Upload error:", err);
      toast.error(err.response?.data?.message || "Failed to upload document.");
    } finally {
      setIsUploading(false);
    }
  };

  const generateSignaturePreview = () => {
    if (!signatureName.trim()) return;
    
    const canvas = document.createElement("canvas");
    canvas.width = 400;
    canvas.height = 100;
    const ctx = canvas.getContext("2d");
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.font = `40px ${selectedFont}`;
    ctx.fillStyle = "#1e293b";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(signatureName, canvas.width / 2, canvas.height / 2);
    
    ctx.beginPath();
    ctx.moveTo(50, 75);
    ctx.lineTo(350, 75);
    ctx.strokeStyle = "#cbd5e1";
    ctx.lineWidth = 1;
    ctx.stroke();

    setSignaturePreview(canvas.toDataURL("image/png"));
  };

  const handleDigitalSignAndSubmit = async () => {
    if (!signaturePreview) {
      toast.error("Please generate a signature first");
      return;
    }
    
    try {
      setIsUploading(true);
      const pdfBlob = await generateSignedPdfBlob(selectedAppForUpload, signaturePreview);
      await handleFileUpload(pdfBlob);
    } catch (err) {
      console.error("Digital sign error:", err);
      toast.error("Failed to generate signed document");
      setIsUploading(false);
    }
  };

  const hasAnyLetters = filtered.length > 0;

  return (
    <div className="emp-dash">
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Dashboard Header */}
        <div className="emp-dash__header">
          <div>
            <h1 className="emp-dash__greeting">
              Official <span>Letters</span>
            </h1>
            <p className="emp-dash__subtitle">
              Secure Document Vault
            </p>
          </div>
          <div className="emp-dash__date-pill">
            <FiFileText />
            <span>
              {new Date().toLocaleDateString("en-US", {
                weekday: "short",
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>
          </div>
        </div>

        {/* Filter Section */}
        <div className="emp-dash__card mb-6">
          <div className="emp-dash__card-header">
            <div>
              <h3 className="emp-dash__card-title flex items-center gap-2">
                <FiSearch className="text-green-600" /> Filter Documents
              </h3>
              <p className="emp-dash__card-desc">Search by role or type</p>
            </div>
          </div>
          <div className="emp-dash__card-body bg-gray-50/50">
            <div className="flex flex-wrap items-center gap-3">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px]">
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by role or type..."
                  value={localSearchQuery}
                  onChange={(e) => setLocalSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 text-sm border border-gray-200 rounded-2xl focus:ring-2 focus:ring-green-500 focus:border-transparent shadow-sm transition-all duration-200 hover:border-gray-300 bg-white"
                />
              </div>

              {/* Reset */}
              {localSearchQuery && (
                <button
                  onClick={() => setLocalSearchQuery("")}
                  className="h-11 px-5 text-sm font-semibold text-gray-700 bg-white border-2 border-red-200 rounded-xl hover:bg-red-50 hover:border-red-300 transition-all duration-200 shadow-md flex items-center gap-2"
                >
                  <FiRefreshCw className="text-sm" /> Clear Filters
                </button>
              )}
            </div>
          </div>
        </div>

      {!hasAnyLetters ? (
        <div className="emp-dash__card py-10">
          <div className="flex flex-col items-center justify-center gap-3">
            <div className="emp-dash__spinner"></div>
            <span className="text-sm font-medium text-gray-500">No documents found</span>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {SECTION_ORDER.map((type) => {
            const cfg = SECTION_CONFIG[type];
            const entries = grouped[type];
            if (entries.length === 0) return null;

            return (
              <div key={type} className="emp-dash__card mb-6">
                <div className="emp-dash__card-header">
                  <div>
                    <h3 className="emp-dash__card-title flex items-center gap-2">
                      <span className="text-green-600">{cfg.icon}</span> {cfg.label}
                    </h3>
                    <p className="emp-dash__card-desc">
                      Showing {entries.length} {entries.length === 1 ? 'letter' : 'letters'}
                    </p>
                  </div>
                </div>

                <div className="emp-dash__table-wrap overflow-x-auto !block">
                  <table className="emp-dash__table min-w-[800px]">
                    <thead>
                      <tr>
                        <th className="py-3 px-4 text-center font-bold">Role / Position</th>
                        <th className="py-3 px-4 text-center font-bold">Document Type</th>
                        <th className="py-3 px-4 text-center font-bold">Issued Date</th>
                        <th className="py-3 px-4 text-center font-bold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((entry) => (
                        <tr key={entry._uid} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-2 text-center">
                            <div className="text-xs font-bold text-gray-800">
                              {entry.jobId?.role || entry.role || "Position"}
                            </div>
                            <div className="text-[9px] text-gray-400 font-bold uppercase tracking-widest mt-0.5">
                              Ref: {entry._id.slice(-6).toUpperCase()}
                            </div>
                          </td>
                          <td className="px-4 py-2 text-center">
                            <span className={`text-[9px] font-bold px-2.5 py-1 rounded-full uppercase tracking-widest ${cfg.badge}`}>
                              {type} Letter
                            </span>
                          </td>
                          <td className="px-4 py-2 text-center">
                            <div className="flex flex-col items-center">
                              <span className="text-[10px] font-bold text-gray-600 tracking-widest uppercase">
                                {new Date(entry.resolvedDate).toLocaleDateString("en-IN", {
                                  day: "numeric", month: "short", year: "numeric"
                                })}
                              </span>
                              <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Verified</span>
                            </div>
                          </td>
                          <td className="px-4 py-2 text-center">
                            <div className="flex items-center justify-center gap-3">
                              <button
                                onClick={() => {
                                  setSelectedOffer({ ...entry, offerLetter: entry.resolvedContent, companyStamp: stampUrl });
                                  setActiveDocTab(type === "Offer" ? "offer" : "agreement");
                                  setIsModalOpen(true);
                                }}
                                className="w-9 h-9 flex items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 hover:bg-indigo-600 hover:text-white transition-all transform active:scale-90 shadow-sm border border-indigo-100"
                                title="View"
                              >
                                <FiEye size={16} />
                              </button>
                              <button
                                onClick={async () => {
                                  if (type === "Resignation") {
                                    handleDownloadRelieving(entry);
                                  } else {
                                    try {
                                      const content = entry.resolvedContent;
                                      const type = entry.resolvedDocType || "Offer";
                                      const sentAt = entry.resolvedDate || new Date();

                                      const doc = new jsPDF();
                                      const pageWidth = doc.internal.pageSize.getWidth();
                                      const pageHeight = doc.internal.pageSize.getHeight();
                                      const margin = 20;
                                      const contentWidth = pageWidth - (2 * margin);

                                      doc.setFillColor(248, 250, 252);
                                      doc.rect(0, 0, pageWidth, 45, 'F');
                                      
                                      const logoBase64 = await getBase64ImageFromUrl(logoUrl);
                                      if (logoBase64 && logoBase64.dataUrl) {
                                        const ratio = logoBase64.width / logoBase64.height;
                                        const imgHeight = 12;
                                        const imgWidth = imgHeight * ratio;
                                        doc.addImage(logoBase64.dataUrl, "PNG", margin, 15, imgWidth, imgHeight);
                                      } else {
                                        doc.setFont("helvetica", "bold");
                                        doc.setFontSize(22);
                                        doc.setTextColor(30, 41, 59);
                                        doc.text("TIMELY HEALTH", margin, 25);
                                      }

                                      const headerTitle = type.toUpperCase();
                                      doc.setFontSize(14);
                                      doc.setFont("helvetica", "bold");
                                      doc.setTextColor(63, 81, 181);
                                      doc.text(`${headerTitle} LETTER`, pageWidth - margin, 25, { align: "right" });

                                      doc.setDrawColor(226, 232, 240);
                                      doc.setLineWidth(0.5);
                                      doc.line(margin, 45, pageWidth - margin, 45);

                                      let currentY = 60;
                                      doc.setFontSize(10);
                                      doc.setTextColor(71, 85, 105);
                                      doc.text(`Issued On: ${new Date(sentAt).toLocaleDateString('en-GB')}`, pageWidth - margin, currentY, { align: "right" });

                                      doc.setFont("helvetica", "bold");
                                      doc.setTextColor(30, 41, 59);
                                      doc.text("TO:", margin, currentY);
                                      doc.setFont("helvetica", "normal");
                                      currentY += 6;
                                      const fullName = profile?.name || `${entry.firstName} ${entry.lastName}`;
                                      doc.text(fullName, margin, currentY);
                                      currentY += 15;

                                      const sanitizedContent = content
                                        .replace(/\*\*/g, "") 
                                        .replace(/\*/g, "")   
                                        .replace(/#{1,6}\s?/g, "")
                                        .replace(/~~/g, "");

                                      doc.setFontSize(11);
                                      doc.setTextColor(51, 65, 85);
                                      const lines = doc.splitTextToSize(sanitizedContent, contentWidth);
                                      
                                      lines.forEach(line => {
                                        if (currentY > pageHeight - 80) {
                                          doc.addPage();
                                          currentY = 30;
                                        }
                                        doc.text(line, margin, currentY);
                                        currentY += 6.5;
                                      });

                                      currentY += 20;
                                      if (currentY > pageHeight - 70) {
                                        doc.addPage();
                                        currentY = 40;
                                      }

                                      doc.setFont("helvetica", "bold");
                                      doc.setFontSize(10);
                                      
                                      if (stampUrl) {
                                        const stampBase64 = await getBase64ImageFromUrl(stampUrl);
                                        if (stampBase64 && stampBase64.dataUrl) {
                                          const stampRatio = stampBase64.width / stampBase64.height;
                                          const stampHeight = 18;
                                          const stampWidth = stampHeight * stampRatio;
                                          doc.addImage(stampBase64.dataUrl, "PNG", margin, currentY - 15, stampWidth, stampHeight);
                                        }
                                      }
                                      
                                      doc.text("Authorized Signatory,", margin, currentY);
                                      currentY += 5;
                                      doc.setFont("helvetica", "normal");
                                      doc.setFontSize(8);
                                      doc.text("Recruitment Division | Timely Health", margin, currentY);
                                      
                                      const rightSide = pageWidth - margin - 60;
                                      doc.setFont("helvetica", "bold");
                                      doc.setFontSize(10);
                                      doc.text("Candidate Signature,", rightSide, currentY - 5);

                                      doc.save(`${type}_Letter_${entry.firstName || "Candidate"}.pdf`);
                                      toast.success("Document downloaded!");
                                    } catch (err) {
                                      console.error("Download failed:", err);
                                      toast.error("Failed to generate PDF");
                                    }
                                  }
                                }}
                                className="w-9 h-9 flex items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white transition-all transform active:scale-90 shadow-sm border border-emerald-100"
                                title="Download"
                              >
                                <FiDownload size={16} />
                              </button>

                              {type !== "Resignation" && (
                                <button
                                  onClick={() => {
                                    setSelectedAppForUpload(entry);
                                    setIsUploadModalOpen(true);
                                  }}
                                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all active:scale-95 shadow-lg border ${
                                    entry.signedPath 
                                      ? 'bg-indigo-50 text-indigo-600 border-indigo-100 shadow-indigo-50/50' 
                                      : 'bg-gray-900 text-white border-black hover:bg-black shadow-gray-200'
                                  }`}
                                  title={entry.signedPath ? "Resubmit" : "Upload"}
                                >
                                  <FiFileText size={14} className={entry.signedPath ? 'text-indigo-400' : 'text-gray-400'} /> 
                                  {entry.signedPath ? "Resubmit" : "Upload"}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}

        {/* Footer */}
        <div className="flex items-center justify-center gap-2 text-[9px] font-bold text-gray-400 uppercase tracking-widest py-4">
          <FiInfo className="text-teal-400" />
          Timely Health Document Vault • {new Date().getFullYear()}
        </div>

      <AnimatePresence>
        {isUploadModalOpen && selectedAppForUpload && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="max-w-md w-full rounded-2xl bg-white shadow-lg border border-gray-200 p-8"
            >
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-sm font-black text-gray-900 uppercase tracking-widest">Sign & Upload</h3>
                  <p className="text-[9px] font-bold text-indigo-500 uppercase tracking-widest mt-1">{selectedAppForUpload.resolvedDocType} Documentation</p>
                </div>
                <button onClick={() => { setIsUploadModalOpen(false); setSelectedFile(null); setSignaturePreview(null); }} className="w-10 h-10 rounded-xl flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors">
                  <FiX />
                </button>
              </div>

              <div className="flex p-1 rounded-2xl mb-6 border border-gray-200">
                <button onClick={() => setSignMethod("upload")} className={`flex-1 py-2 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${signMethod === "upload" ? "text-indigo-600 shadow-sm" : "text-gray-400 hover:text-gray-600"}`}>Upload File</button>
                <button onClick={() => setSignMethod("digital")} className={`flex-1 py-2 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${signMethod === "digital" ? "text-indigo-600 shadow-sm" : "text-gray-400 hover:text-gray-600"}`}>Digital Sign</button>
              </div>

              {signMethod === "upload" ? (
                <>
                  <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest leading-relaxed mb-6">Please download the document, sign it physically or digitally, and upload the scanned copy (PDF/Image) here.</p>
                  <div className={`relative border-2 border-dashed rounded-2xl p-10 flex flex-col items-center justify-center transition-all ${selectedFile ? 'border-indigo-400' : 'border-gray-100 hover:border-indigo-300'}`}>
                    <input type="file" onChange={(e) => setSelectedFile(e.target.files[0])} className="absolute inset-0 opacity-0 cursor-pointer" accept=".pdf,.doc,.docx,.jpg,.png" />
                    <FaFileContract className={`text-4xl mb-3 ${selectedFile ? 'text-indigo-500' : 'text-gray-300'}`} />
                    <p className="text-xs font-bold text-gray-800">{selectedFile ? selectedFile.name : "Choose File or Drag & Drop"}</p>
                    <p className="text-[9px] font-bold text-gray-400 uppercase mt-1">PDF, DOC, JPG up to 5MB</p>
                  </div>
                </>
              ) : (
                <div className="space-y-6">
                  <div>
                    <label className="block text-[9px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Type Your Full Name</label>
                    <input type="text" className="w-full px-4 py-3 border border-gray-100 rounded-2xl text-xs font-bold focus:ring-4 focus:ring-indigo-500/5 outline-none" placeholder="e.g. Johnathan Doe" value={signatureName} onChange={(e) => setSignatureName(e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-[9px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Select Signature Style</label>
                    <div className="grid grid-cols-2 gap-2">
                      {[{ name: "Script", font: "'Dancing Script', cursive" }, { name: "Cursive", font: "'Pacifico', cursive" }, { name: "Formal", font: "'Great Vibes', cursive" }, { name: "Modern", font: "'Satisfy', cursive" }].map(style => (
                        <button key={style.name} onClick={() => setSelectedFont(style.font)} className={`py-2 border rounded-xl text-[10px] font-bold transition-all ${selectedFont === style.font ? 'text-indigo-600 border-indigo-600' : 'text-gray-600 border-gray-100 hover:border-indigo-200'}`} style={{ fontFamily: style.font }}>{style.name} Style</button>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-center">
                    <button onClick={generateSignaturePreview} disabled={!signatureName.trim()} className="px-6 py-2 text-indigo-600 rounded-xl text-[9px] font-black uppercase tracking-widest hover:text-indigo-700 disabled:opacity-50">Generate Signature</button>
                  </div>
                  {signaturePreview && (
                    <div className="p-8 bg-slate-50 border border-slate-200 rounded-[2rem] flex items-center justify-center min-h-[200px] shadow-inner">
                      <img src={signaturePreview} alt="Signature Preview" className="max-h-40 object-contain drop-shadow-md" />
                    </div>
                  )}
                </div>
              )}

              <div className="mt-8 flex gap-3">
                <button onClick={() => { setIsUploadModalOpen(false); setSelectedFile(null); setSignaturePreview(null); }} className="flex-1 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest rounded-2xl hover:bg-gray-50 transition-colors">Cancel</button>
                {signMethod === "upload" ? (
                  <button onClick={() => handleFileUpload()} disabled={!selectedFile || isUploading} className="flex-1 py-4 bg-gradient-to-r from-green-500 to-blue-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] shadow-lg shadow-blue-100 hover:scale-[1.02] transition-all active:scale-95 disabled:opacity-50">
                    {isUploading ? "Transmitting..." : "Execute Upload"}
                  </button>
                ) : (
                  <button onClick={handleDigitalSignAndSubmit} disabled={!signaturePreview || isUploading} className="flex-1 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] shadow-lg shadow-indigo-100 hover:scale-[1.02] transition-all active:scale-95 disabled:opacity-50">
                    {isUploading ? "Signing..." : "Add & Submit"}
                  </button>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {isResignModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 max-w-md w-full p-8">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-sm font-black text-gray-900 uppercase tracking-widest">File Resignation</h3>
                <p className="text-[9px] font-bold text-red-500 uppercase tracking-widest mt-1">Submit resignation letter</p>
              </div>
              <button onClick={() => setIsResignModalOpen(false)} className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors">
                <FiX />
              </button>
            </div>
            <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-4">Please provide a brief letter of resignation. This action cannot be easily reversed.</p>
            <textarea value={resignationLetter} onChange={(e) => setResignationLetter(e.target.value)} className="w-full h-32 p-3 text-xs font-medium border border-gray-200 rounded-2xl focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none mb-4 resize-none bg-gray-50/50" placeholder="Type your formal resignation letter here..." />
            <div className="flex gap-3">
              <button onClick={() => setIsResignModalOpen(false)} className="flex-1 py-3 text-[10px] font-black text-gray-400 uppercase tracking-widest rounded-xl hover:bg-gray-50 transition-colors">Cancel</button>
              <button onClick={handleResign} disabled={!resignationLetter.trim() || isSubmittingResign} className="flex-1 py-3 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-red-100 hover:scale-[1.02] transition-all active:scale-95 disabled:opacity-50">
                {isSubmittingResign ? "Submitting..." : "Submit Resignation"}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Letters;